import { cloneDeep } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useDebounce from "app/hooks/useDebounce";
import { showToast } from "utils/toast";

import {
  refreshDeviceAlertList,
  toggleFetchTableData,
} from "app/store/patient/slice/patientSlice";
import {
  transmissionTableApi,
  useCreatePatientMutation,
  useSaveDeviceInformationMutation,
} from "app/store/transmission/api/transmissionTableApi";
import { changeProfileField } from "app/store/transmission/slice/transmissionSlice";
import useCheckSaasAccountUser from "hooks/useCheckSaasAccountUser";
import { getCurrentUserRole } from "utils/currentUser.util";
import { convertRpmDeviceObject } from "utils/rpmDevice";
import { USER_ROLES } from "static/constants";
import { useGetManufacturersQuery } from "app/store/manufacturers/api/manufacturersApi";
import { useFetchAccountListQuery } from "app/store/dashboard/doctor_analytic/api/doctorAnalyticApi";
import { isValidArray } from "utils/array";

const useDeviceInformation = () => {
  const {
    profile,
    information,
    deviceInformation,
    patientSerialNumber,
    originalDeviceInformation,
    secondaryDeviceInformation,
    originalSecondaryDeviceInformation,
  } = useSelector((state) => state.transmission);

  const [selectedDevice, setSelectedDevice] = useState({});
  const [isDuplicatePatientConfirmModal, setIsDuplicatePatientConfirmModal] =
    useState(false);

  const initialDeviceInformation = {
    account: "",
    manufacturer: "",
    modelName: "",
    modelNumber: "",
    doi: "",
  };

  const defaultDeviceInformation = {
    ...initialDeviceInformation,
    ...deviceInformation,
  };

  const defaultSecondaryDeviceInformation = {
    ...initialDeviceInformation,
    ...secondaryDeviceInformation,
  };

  const newDeviceInformationInitialValue = {
    modelName: "",
    modelNumber: "",
    alertDevice: false,
    alertDeviceText: "",
    mriCompatible: false,
    leads: [],
    rpm_devices: [],
    implanting_physicians: [],
  };

  const deviceInformationEditAccessRoles = [
    USER_ROLES.ADMIN,
    USER_ROLES.USER,
    USER_ROLES.CLINIC_ADMIN,
    USER_ROLES.CLINIC,
    USER_ROLES.PHYSICIAN,
  ];

  const dispatch = useDispatch();
  const currentUserRole = getCurrentUserRole();
  const isSaasAccountUser = useCheckSaasAccountUser();
  const { data: accounts } = useFetchAccountListQuery();
  const { data: manufacturerList } = useGetManufacturersQuery();

  const [createPatient] = useCreatePatientMutation();
  const [saveDeviceInformation] = useSaveDeviceInformationMutation();

  const [isLoading, setIsLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previousDevices, setPreviousDevices] = useState([]);
  const [showSecondaryDevice, setShowSecondaryDevice] = useState(false);
  const [isCreatePatientModal, setIsCreatePatientModal] = useState(false);
  const [showAddNewDeviceModal, setShowAddNewDeviceModal] = useState(false);
  const [primaryDevice, setPrimaryDevice] = useState(defaultDeviceInformation);

  const [showAddNewDeviceSuccessPrompt, setShowAddNewDeviceSuccessPrompt] =
    useState(false);
  const [secondaryDevice, setSecondaryDevice] = useState(
    defaultSecondaryDeviceInformation
  );
  const [isSecondaryDeviceEditMode, setIsSecondaryDeviceEditMode] =
    useState(false);
  const [isSecondaryDeviceSubmitLoading, setIsSecondaryDeviceSubmitLoading] =
    useState(false);
  const [hasIncompleteRpmInformation, setHasIncompleteRpmInformation] =
    useState(false);
  const [newDeviceInformation, setNewDeviceInformation] = useState(
    newDeviceInformationInitialValue
  );
  const [newSecondaryDeviceInformation, setNewSecondaryDeviceInformation] =
    useState(newDeviceInformationInitialValue);

  const [devices, setDevices] = useState([]);

  const loadPrimaryDeviceFromProfile = useCallback(() => {
    if (profile) {
      const deviceInformationData = {
        ...primaryDevice,
        doi: profile?.doi,
        account: profile?.account,
        manufacturer: profile?.manufacturer,
        serial_number: profile?.serial_number,
        modelName: primaryDevice?.modelName,
      };

      setPrimaryDevice({
        ...deviceInformationData,
      });

      setNewDeviceInformation(deviceInformationData);
      setPreviousDevices(profile?.previous_devices);
    }
  }, [profile]);

  const loadSecondaryDeviceFromProfile = useCallback(() => {
    if (profile) {
      const deviceInformationData = {
        ...secondaryDevice,
        doi: secondaryDeviceInformation?.doi,
        manufacturer: secondaryDeviceInformation?.manufacturer,
        account: secondaryDeviceInformation?.account || profile?.account,
        modelName:
          secondaryDeviceInformation?.deviceInformation?.model_display_name ||
          secondaryDeviceInformation?.deviceInformation?.modelName,
        modelNumber: secondaryDeviceInformation?.deviceInformation?.modelNumber,
        mriCompatible:
          secondaryDeviceInformation?.deviceInformation?.mriCompatible,
        alertDeviceText:
          secondaryDeviceInformation?.deviceInformation?.alertDeviceText,
        alertDevice: secondaryDeviceInformation?.deviceInformation?.alertDevice,
        implanting_physicians:
          secondaryDeviceInformation?.deviceInformation?.implanting_physicians,
      };

      setShowSecondaryDevice(
        secondaryDeviceInformation
          ? Object.keys(secondaryDeviceInformation).length > 0
          : false
      );

      setSecondaryDevice({ ...deviceInformationData });
      setNewSecondaryDeviceInformation({ ...deviceInformationData });
    }
  }, [profile]);

  const loadCombineDevices = useCallback(() => {
    let deviceArray = [];
    const newPreviousDevices = (profile?.previous_devices || [])?.map(
      (device) => ({
        ...device,
        device_type: "Previous",
      })
    );

    deviceArray = newPreviousDevices;

    if (secondaryDevice?.modelName || secondaryDevice?.modelNumber) {
      deviceArray.unshift({
        ...secondaryDevice,
        device_model: secondaryDevice?.modelName,
        device_serial: secondaryDevice?.modelNumber,
        device_type: "Secondary",
      });
    }

    if (deviceInformation?.modelName || deviceInformation?.modelNumber) {
      deviceArray.unshift({
        account: profile?.account,
        manufacturer: profile?.manufacturer,
        device_model: deviceInformation?.modelName,
        device_serial: deviceInformation?.modelNumber,
        doi: profile?.doi,
        implanting_physicians: deviceInformation?.implanting_physicians,
        device_type: "Primary",
      });
    }

    setDevices(deviceArray);
  }, [profile, secondaryDevice, deviceInformation]);

  useEffect(() => {
    if (deviceInformation.rpm_devices) {
      checkHasIncompleteRpmInformation(deviceInformation.rpm_devices);
    }
  }, [deviceInformation.rpm_devices]);

  useEffect(() => {
    loadCombineDevices();
  }, [profile, secondaryDevice, deviceInformation]);

  useEffect(() => {
    loadPrimaryDeviceFromProfile();
    loadSecondaryDeviceFromProfile();

    return () => {
      loadPrimaryDeviceFromProfile();
      loadSecondaryDeviceFromProfile();
    };
  }, []);

  const debounceDispatch = useCallback(
    useDebounce((value, type) => {
      dispatch(
        changeProfileField({
          type: type,
          value: value,
        })
      );
    }, 500),
    []
  );

  const handleInputChange = async ({ values, isProfile, isPrimaryDevice }) => {
    if (isProfile) {
      const deviceInformationData = {
        doi: values?.doi || profile?.doi,
        account: values?.account || profile?.account,
        manufacturer: values?.manufacturer || profile?.manufacturer,
        ...newDeviceInformation,
      };

      setPrimaryDevice({ ...deviceInformationData });
      debounceDispatch(
        {
          ...profile,
          ...deviceInformationData,
          previous_devices: previousDevices || [],
        },
        deviceInformationData,
        "profile"
      );
    } else {
      const newDeviceInformationValue = { ...values };
      const stateName = isPrimaryDevice
        ? "deviceInformation"
        : "secondaryDeviceInformation";

      isPrimaryDevice
        ? setNewDeviceInformation(newDeviceInformationValue)
        : setNewSecondaryDeviceInformation(newDeviceInformationValue);
      debounceDispatch(values, stateName);
    }
  };

  const handleCancel = async (isPrimaryDevice) => {
    const deviceInformation = isPrimaryDevice
      ? originalDeviceInformation
      : originalSecondaryDeviceInformation;

    const originalDeviceInfoCopy = cloneDeep(deviceInformation);

    const inputChangeArray = [
      handleInputChange({ values: originalDeviceInfoCopy, isPrimaryDevice }),
    ];
    await Promise.all(inputChangeArray);
  };

  const handleDeviceInformation = async (isPrimaryDevice) => {
    try {
      // Set initialDeviceInformation to send empty strings for fields without values instead of not sending them at all.
      const deviceInfoPayload = {
        ...initialDeviceInformation,
        ...(isPrimaryDevice ? deviceInformation : secondaryDeviceInformation),
      };

      const { rpm_devices = [], ...updatedDeviceInfoPayload } =
        deviceInfoPayload || {};

      const filteredRpmDevices = isValidArray(rpm_devices)
        ? rpm_devices
            ?.filter((rpmDevice) => rpmDevice.imei !== "")
            .map(({ isImeiRemovedByUser, ...rest }) => rest)
        : [];

      const payload = {
        isPrimaryDevice,
        ...(!isPrimaryDevice && { account: updatedDeviceInfoPayload.account }),
        ...(isPrimaryDevice && {
          rpm_devices: filteredRpmDevices,
        }),
        patient: patientSerialNumber,
        doi: updatedDeviceInfoPayload.doi || "",
        manufacturer: isPrimaryDevice
          ? profile?.manufacturer
          : updatedDeviceInfoPayload.manufacturer || "",
        deviceInformation: {
          alertDevice: updatedDeviceInfoPayload?.alertDevice,
          alertDeviceText: updatedDeviceInfoPayload?.alertDeviceText,
          lead1: updatedDeviceInfoPayload?.lead1,
          lead2: updatedDeviceInfoPayload?.lead2,
          lead3: updatedDeviceInfoPayload?.lead3,
          mriCompatible: updatedDeviceInfoPayload?.mriCompatible,
          prevDevice: updatedDeviceInfoPayload?.prevDevice,
          modelName:
            updatedDeviceInfoPayload?.modelName ||
            updatedDeviceInfoPayload?.deviceInformation?.modelName ||
            "",
          modelNumber:
            updatedDeviceInfoPayload?.modelNumber ||
            updatedDeviceInfoPayload?.deviceInformation?.modelNumber ||
            "",
          implanting_physicians:
            updatedDeviceInfoPayload?.implanting_physicians || [],
        },
      };

      const response = await saveDeviceInformation(payload).unwrap();

      const isDeviceAlertUpdated =
        originalDeviceInformation.alertDeviceText !==
          deviceInformation.alertDeviceText ||
        originalDeviceInformation.alertDevice !== deviceInformation.alertDevice;

      if (isDeviceAlertUpdated) {
        dispatch(refreshDeviceAlertList(true));
        await dispatch(toggleFetchTableData(true));
      }

      if (!response.error) {
        const rpmDevicesPayload = isValidArray(rpm_devices)
          ? convertRpmDeviceList(rpm_devices)
          : {};
        const updatedValue = {
          ...(isPrimaryDevice ? deviceInfoPayload : payload),
          ...(isPrimaryDevice && { rpm_devices: filteredRpmDevices }),
        };

        handleProfileFieldUpdates(
          updatedValue,
          rpmDevicesPayload,
          isPrimaryDevice
        );
      }
      return response;
    } catch (error) {
      console.error("Error", error);
      throw error;
    } finally {
      dispatch(toggleFetchTableData(false));
      dispatch(refreshDeviceAlertList(false));
    }
  };

  const handleProfileFieldUpdates = (
    updatedValue,
    rpmDevicesPayload,
    isPrimaryDevice
  ) => {
    dispatch(
      changeProfileField({
        type: isPrimaryDevice
          ? "deviceInformation"
          : "secondaryDeviceInformation",
        value: updatedValue,
      })
    );

    dispatch(
      changeProfileField({
        type: isPrimaryDevice
          ? "originalDeviceInformation"
          : "originalSecondaryDeviceInformation",
        value: updatedValue,
      })
    );

    if (isPrimaryDevice) {
      dispatch(
        changeProfileField({
          type: "originalProfile",
          value: {
            ...profile,
            rpm_devices: { ...profile.rpm_devices, ...rpmDevicesPayload },
          },
        })
      );

      dispatch(
        changeProfileField({
          type: "profile",
          value: {
            ...profile,
            ...(updatedValue?.doi && { doi: updatedValue?.doi }),
            ...(updatedValue?.serial_number && {
              serial_number: updatedValue?.serial_number,
            }),

            rpm_devices: { ...profile.rpm_devices, ...rpmDevicesPayload },
          },
        })
      );
    }
  };

  const convertRpmDeviceList = (rpmDeviceList) => {
    const rpmDeviceData = {};
    rpmDeviceList?.forEach((rpmDevice) => {
      const { rpmDeviceKey, imei, active } = rpmDevice;
      rpmDeviceData[rpmDeviceKey] = { imei, active };
    });
    return rpmDeviceData;
  };

  const checkHasIncompleteRpmInformation = (rpmDevices) => {
    const hasEmptyValues = rpmDevices?.some(
      (device) => !device.imei || !device.rpmDeviceName
    );
    setHasIncompleteRpmInformation(hasEmptyValues);
  };

  const handleSubmit = async (isPrimaryDevice) => {
    try {
      isPrimaryDevice
        ? setIsLoading(true)
        : setIsSecondaryDeviceSubmitLoading(true);

      const response = await handleDeviceInformation(!!isPrimaryDevice);

      if (response.error) throw new Error(response.message);

      showToast({ message: response?.message });

      isPrimaryDevice
        ? setIsEditMode(false)
        : setIsSecondaryDeviceEditMode(false);

      const deviceInformationValue = isPrimaryDevice
        ? response?.patient?.deviceInformation
        : secondaryDeviceInformation;

      const newDeviceData = {
        ...(isPrimaryDevice ? primaryDevice : secondaryDevice),
        doi: deviceInformationValue?.doi || profile?.doi,
        account: deviceInformationValue?.account || profile?.account,
        manufacturer:
          deviceInformationValue?.manufacturer || profile?.manufacturer,
        modelName: deviceInformationValue?.modelName,
        modelNumber: deviceInformationValue?.modelNumber,
        rpm_devices: deviceInformationValue?.rpm_devices,
        alertDeviceText: deviceInformationValue?.alertDeviceText,
        implanting_physicians: deviceInformationValue?.implanting_physicians,
      };

      if (isPrimaryDevice) {
        setPrimaryDevice(newDeviceData);
        setNewDeviceInformation(newDeviceData);

        dispatch(
          changeProfileField({
            type: "deviceInformation",
            value: deviceInformationValue,
          })
        );
      } else {
        setSecondaryDevice(newDeviceData);
        setNewSecondaryDeviceInformation(newDeviceData);
      }

      await dispatch(toggleFetchTableData(true));
    } catch (error) {
      const errors = error?.message?.split(". ");
      errors.map((errorMessage) =>
        showToast({ message: errorMessage, type: "error" })
      );
    } finally {
      isPrimaryDevice
        ? setIsLoading(false)
        : setIsSecondaryDeviceSubmitLoading(false);
      dispatch(toggleFetchTableData(false));
    }
  };

  const handleCancelClick = (isPrimaryCancel = false) => {
    handleCancel(isPrimaryCancel);
    isPrimaryCancel
      ? setIsEditMode(false)
      : setIsSecondaryDeviceEditMode(false);
  };

  const handleAddNewDeviceModalClick = async () => {
    setShowAddNewDeviceModal(true);
  };

  const handleAddNewDevice = (newDeviceInfo) => {
    try {
      setIsLoading(true);
      dispatch(
        transmissionTableApi.util.invalidateTags([
          "patientLeadInformation",
          profile?.patientId,
        ])
      );

      const newDeviceData = {
        ...newDeviceInfo?.deviceInformation,
        account: newDeviceInfo?.account,
        manufacturer: newDeviceInfo?.manufacturer,
        doi: newDeviceInfo?.doi,
        rpm_devices: primaryDevice?.rpm_devices,
        modelName:
          newDeviceInfo?.deviceInformation?.model_display_name ||
          newDeviceInfo?.deviceInformation?.modelName,
        modelNumber: newDeviceInfo?.deviceInformation?.modelNumber,
      };

      const profileData = {
        ...profile,
        ...newDeviceData,
        physicians:
          newDeviceInfo?.account === profile?.account
            ? newDeviceInfo?.physicians
            : [],
        deviceInformation: newDeviceInfo?.deviceInformation,
        previous_devices: newDeviceInfo?.previousDevices,
      };

      const newPreviousDevices = newDeviceInfo?.previousDevices?.map(
        (device) => ({
          ...device,
          device_model: device?.model_display_name || device?.device_model,
        })
      );

      setPrimaryDevice(newDeviceData);
      setPreviousDevices(newPreviousDevices);
      setShowAddNewDeviceModal(false);
      setShowAddNewDeviceSuccessPrompt(true);

      dispatch(
        changeProfileField({
          type: "deviceInformation",
          value: newDeviceInfo?.deviceInformation,
        })
      );

      dispatch(
        changeProfileField({
          type: "originalDeviceInformation",
          value: newDeviceInfo?.deviceInformation,
        })
      );

      dispatch(
        changeProfileField({
          type: "originalProfile",
          value: profileData,
        })
      );

      dispatch(
        changeProfileField({
          type: "profile",
          value: profileData,
        })
      );

      dispatch(transmissionTableApi.util.invalidateTags(["getAlertBanner"]));
    } catch (error) {
      console.error("Error", error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePatient = async () => {
    try {
      setIsSubmitting(true);
      const deviceType = selectedDevice?.device_type?.toLowerCase() || "";

      const payload = {
        serialNumber: profile?.serial_number,
        modelName:
          selectedDevice?.model_display_name || selectedDevice?.modelName || "",
        modelNumber: selectedDevice?.modelNumber || "",
        deviceType,
      };

      const response = await createPatient(payload);

      if (response?.error) {
        throw response?.error;
      }

      if (!response?.error) {
        showToast({ message: response?.data?.message });

        const oldPatient = response?.data?.data?.oldPatient;
        const oldPatientDeviceInformation = oldPatient?.deviceInformation;
        const oldSecondaryDevice = oldPatient?.secondary_device_information;

        const oldSecondaryDeviceInformation = oldSecondaryDevice
          ? {
              ...oldSecondaryDevice,
            }
          : {};

        const patientId = oldPatient?._id;
        const rpmDevices = convertRpmDeviceObject(oldPatient?.rpm_devices);

        setPrimaryDevice({
          ...oldPatientDeviceInformation,
          account: oldPatient?.account,
          manufacturer: oldPatient?.manufacturer,
          doi: oldPatient?.doi,
          rpm_devices: rpmDevices,
          model_display_name:
            oldPatientDeviceInformation?.model_display_name ||
            oldPatientDeviceInformation?.modelName,
        });

        const secondaryDeviceObj = {
          ...oldSecondaryDeviceInformation?.deviceInformation,
          doi: oldSecondaryDeviceInformation?.doi,
          account: oldSecondaryDeviceInformation?.account,
          manufacturer: oldSecondaryDeviceInformation?.manufacturer,
          model_display_name:
            oldSecondaryDeviceInformation?.model_display_name ||
            oldSecondaryDeviceInformation?.modelName,
        };

        setSecondaryDevice({ ...secondaryDeviceObj });

        const hasSecondaryDevice =
          Object.keys(oldSecondaryDeviceInformation)?.length > 0 ? true : false;

        const oldPreviousDevices = oldPatient?.previous_devices?.map(
          (device) => ({
            ...device,
            device_model: device?.model_display_name || device?.device_model,
          })
        );

        setShowSecondaryDevice(hasSecondaryDevice);

        setPreviousDevices(oldPreviousDevices);

        dispatch(
          changeProfileField({
            type: "deviceInformation",
            value: oldPatientDeviceInformation,
          })
        );

        dispatch(
          changeProfileField({
            type: "originalDeviceInformation",
            value: oldPatientDeviceInformation,
          })
        );

        dispatch(
          changeProfileField({
            type: "originalProfile",
            value: {
              ...oldPatient,
              physicians:
                oldPatient?.information?.physicians?.map(
                  (physicianObject) => physicianObject?.user_id
                ) || [],
              patientId,
              rpm_devices: rpmDevices,
            },
          })
        );

        dispatch(
          changeProfileField({
            type: "profile",
            value: {
              ...oldPatient,
              physicians:
                oldPatient?.information?.physicians?.map(
                  (physicianObject) => physicianObject?.user_id
                ) || [],
              patientId,
              rpm_devices: rpmDevices,
            },
          })
        );

        dispatch(
          changeProfileField({
            type: "secondaryDeviceInformation",
            value: oldSecondaryDeviceInformation,
          })
        );

        dispatch(
          changeProfileField({
            type: "originalSecondaryDeviceInformation",
            value: oldSecondaryDeviceInformation,
          })
        );

        dispatch(
          transmissionTableApi.util.invalidateTags([
            "patientProfile",
            profile?.serial_number,
          ])
        );
      }
    } catch (error) {
      showToast({ message: error.message, type: "error" });
    } finally {
      setIsCreatePatientModal(false);
      setIsSubmitting(false);
      setIsDuplicatePatientConfirmModal(false);
      setSelectedDevice({});
    }
  };

  return {
    profile,
    accounts,
    isLoading,
    isEditMode,
    information,
    isSubmitting,
    primaryDevice,
    previousDevices,
    currentUserRole,
    secondaryDevice,
    manufacturerList,
    isSaasAccountUser,
    deviceInformation,
    showSecondaryDevice,
    isCreatePatientModal,
    newDeviceInformation,
    showAddNewDeviceModal,
    originalDeviceInformation,
    isSecondaryDeviceEditMode,
    secondaryDeviceInformation,
    hasIncompleteRpmInformation,
    showAddNewDeviceSuccessPrompt,
    newSecondaryDeviceInformation,
    isSecondaryDeviceSubmitLoading,
    deviceInformationEditAccessRoles,

    dispatch,
    handleCancel,
    handleSubmit,
    setIsEditMode,
    handleCancelClick,
    handleInputChange,
    setSecondaryDevice,
    handleAddNewDevice,
    handleCreatePatient,
    setShowSecondaryDevice,
    setIsCreatePatientModal,
    handleDeviceInformation,
    setNewDeviceInformation,
    setShowAddNewDeviceModal,
    handleAddNewDeviceModalClick,
    setIsSecondaryDeviceEditMode,
    setShowAddNewDeviceSuccessPrompt,
    setNewSecondaryDeviceInformation,
    devices,

    selectedDevice,
    setSelectedDevice,

    isDuplicatePatientConfirmModal,
    setIsDuplicatePatientConfirmModal,
  };
};

export default useDeviceInformation;
